import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TICKET_STATUSES, TICKET_PRIORITIES, statusMeta, priorityMeta, formatDateShort } from "@/lib/tickets";
import { Plus, Search, Filter, Bookmark, X as IconX, Bell, RefreshCw } from "lucide-react";
import MultiSelect from "@/components/MultiSelect";
import SearchSelect from "@/components/SearchSelect";
import SortableTh, { sortRows } from "@/components/SortableTh";
import { toast } from "sonner";

export default function TicketsList() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const isStaff = ["agent", "admin", "superadmin"].includes(user?.role);
    const isSuperCustomer = user?.role === "super_customer";
    const showRequesterCol = isStaff || isSuperCustomer;
    const [rows, setRows] = useState([]);
    const [filters, setFilters] = useState({ status: [], priority: [], assignee: "", requester: "", company_id: "", q: "", waiting_state: "", internal: "" });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [agentList, setAgentList] = useState([]);
    const [customerList, setCustomerList] = useState([]);
    const [companyList, setCompanyList] = useState([]);
    const [savedViews, setSavedViews] = useState([]);
    const [viewCounts, setViewCounts] = useState({});
    const prevCountsRef = useRef(null);   // last seen counts for diff
    const initialLoadRef = useRef(true);  // skip toast on first load
    const [savingView, setSavingView] = useState(false);
    const [sort, setSort] = useState({ field: "updated_at", dir: "desc" });

    const load = useCallback(async () => {
        setLoading(true); setError("");
        try {
            const params = {};
            Object.entries(filters).forEach(([k, v]) => {
                if (v === null || v === undefined || v === "") return;
                if (Array.isArray(v)) {
                    if (v.length > 0) params[k] = v.join(",");
                } else {
                    params[k] = v;
                }
            });
            const { data } = await api.get("/tickets", { params });
            setRows(data);
            // Refresh view counts in background (cheap; per-view count_documents)
            if (isStaff) {
                api.get("/views/counts").then((r) => applyCounts(r.data || {}, savedViews)).catch(() => {});
            }
        } catch (err) { setError(formatApiError(err)); }
        finally { setLoading(false); }
    }, [filters, isStaff]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        // Load companies for ALL roles so we can render the Empresa column.
        // Use scope=mine so the filter dropdown only shows companies the user actually belongs to.
        (async () => {
            try {
                const c = await api.get("/companies", { params: { scope: "mine" } });
                setCompanyList(c.data.filter((x) => x.is_special !== "all"));
            } catch { /* ignore */ }
        })();
    }, []);

    useEffect(() => {
        if (!isStaff) return;
        (async () => {
            try {
                const [u, v, counts] = await Promise.all([
                    api.get("/users"),
                    api.get("/views"),
                    api.get("/views/counts"),
                ]);
                setAgentList(u.data.filter((x) => ["agent", "admin", "superadmin"].includes(x.role)));
                setCustomerList(u.data.filter((x) => x.role === "customer"));
                setSavedViews(v.data || []);
                applyCounts(counts.data || {}, v.data || []);
            } catch { /* non-blocking */ }
        })();
    }, [isStaff]); // eslint-disable-line react-hooks/exhaustive-deps

    // Poll view counts every 30s; toasts when a view's count grows
    useEffect(() => {
        if (!isStaff) return;
        const t = setInterval(() => {
            api.get("/views/counts")
                .then((r) => applyCounts(r.data || {}, savedViews))
                .catch(() => {});
        }, 30000);
        return () => clearInterval(t);
    }, [isStaff, savedViews]); // eslint-disable-line react-hooks/exhaustive-deps

    const reloadViews = async () => {
        try {
            const [v, counts] = await Promise.all([api.get("/views"), api.get("/views/counts")]);
            setSavedViews(v.data || []);
            applyCounts(counts.data || {}, v.data || []);
        } catch { /* ignore */ }
    };

    const applyCounts = (newCounts, viewsList) => {
        const prev = prevCountsRef.current;
        // On first load just store, no toast
        if (prev && !initialLoadRef.current) {
            const list = viewsList || savedViews;
            for (const v of list) {
                const before = prev[v.view_id];
                const after = newCounts[v.view_id];
                if (typeof before === "number" && typeof after === "number" && after > before) {
                    const delta = after - before;
                    toast(`Tienes ${delta} ticket${delta > 1 ? "s" : ""} nuevo${delta > 1 ? "s" : ""} en "${v.name}"`, {
                        description: `Total ahora: ${after}`,
                    });
                }
            }
        }
        prevCountsRef.current = newCounts;
        initialLoadRef.current = false;
        setViewCounts(newCounts);
    };

    const saveCurrentView = async () => {
        const name = window.prompt("Nombre de la vista (máx 60 caracteres):", "");
        if (!name || !name.trim()) return;
        setSavingView(true); setError("");
        try {
            await api.post("/views", { name: name.trim(), filters });
            await reloadViews();
        } catch (err) { setError(formatApiError(err)); }
        finally { setSavingView(false); }
    };

    const applyView = (view) => {
        const toArr = (v) => {
            if (!v) return [];
            if (Array.isArray(v)) return v;
            return String(v).split(",").map(s => s.trim()).filter(Boolean);
        };
        setFilters({
            status: toArr(view.filters.status),
            priority: toArr(view.filters.priority),
            assignee: view.filters.assignee || "",
            requester: view.filters.requester || "",
            company_id: view.filters.company_id || "",
            q: view.filters.q || "",
            waiting_state: view.filters.waiting_state || "",
            internal: view.filters.internal || "",
        });
    };

    const removeView = async (view) => {
        if (!window.confirm(`¿Eliminar la vista "${view.name}"?`)) return;
        try { await api.delete(`/views/${view.view_id}`); await reloadViews(); }
        catch (err) { setError(formatApiError(err)); }
    };

    const toggleViewEmailAlert = async (view) => {
        const next = !view.email_alert_enabled;
        try {
            await api.patch(`/views/${view.view_id}`, { email_alert_enabled: next });
            toast(next
                ? `Alertas por email activadas para "${view.name}"`
                : `Alertas por email desactivadas para "${view.name}"`);
            await reloadViews();
        } catch (err) { setError(formatApiError(err)); }
    };

    const companyById = useMemo(() => {
        const m = {};
        for (const c of companyList) m[c.company_id] = c;
        return m;
    }, [companyList]);

    const sortedRows = useMemo(() => {
        return sortRows(rows, sort, {
            company: (t) => companyById?.[t.company_id]?.name || "",
            priority: (t) => ({ urgent: 4, high: 3, medium: 2, low: 1 })[t.priority] || 0,
            requester_name: (t) => t.requester_name || "",
            assignee_name: (t) => t.assignee_name || "",
        });
    }, [rows, sort, companyById]);

    const quickChips = useMemo(() => {
        if (!isStaff) return [];
        return [
            { k: "Asignados a mí", f: { assignee: "me" } },
            { k: "Sin asignar", f: { assignee: "unassigned" } },
            { k: "Nuevos", f: { status: "new" } },
            { k: "Urgentes", f: { priority: "urgent" } },
        ];
    }, [isStaff]);

    const hasAnyFilter = filters.status || filters.priority || filters.assignee || filters.requester || filters.company_id || filters.q || filters.waiting_state || filters.internal;

    // "Operando sobre N empresa(s): A · B · C" — recordatorio del alcance para staff no-superadmin.
    // companyList ya viene filtrado por scope=mine, así que es exactamente lo que el usuario puede ver.
    const showScopeHint = isStaff && user?.role !== "superadmin" && companyList.length > 0;
    const scopeCompanyNames = companyList.map((c) => c.name);

    return (
        <div data-testid="tickets-page">
            <div className="page-head">
                <div>
                    <h1>Tickets</h1>
                    <p>{isStaff ? "Bandeja colaborativa de tickets." : "Tus solicitudes de soporte."}</p>
                    {showScopeHint && (
                        <p
                            data-testid="tickets-scope-hint"
                            style={{ marginTop: 6, fontSize: 12, color: "var(--text-muted)", display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6 }}
                        >
                            <span style={{ textTransform: "uppercase", letterSpacing: ".06em", fontWeight: 600, fontSize: 11 }}>
                                Operando sobre {scopeCompanyNames.length} empresa{scopeCompanyNames.length === 1 ? "" : "s"}:
                            </span>
                            {scopeCompanyNames.map((n, i) => (
                                <span key={n} style={{ color: "var(--text)" }}>
                                    {n}{i < scopeCompanyNames.length - 1 ? <span style={{ color: "var(--text-dim)", margin: "0 4px" }}>·</span> : null}
                                </span>
                            ))}
                        </p>
                    )}
                </div>
                <button className="btn btn-primary" onClick={() => navigate("/tickets/new")} data-testid="ticket-new-btn">
                    <Plus size={16} /> Nuevo ticket
                </button>
                <button className="btn btn-ghost" onClick={load} disabled={loading} data-testid="tickets-refresh-btn" title="Recargar bandeja" style={{ marginLeft: 8 }}>
                    <RefreshCw size={16} className={loading ? "spin" : ""} />
                </button>
            </div>

            {error && <div className="alert alert-error" data-testid="tickets-error">{error}</div>}

            <div className="card">
                <div className="card-header" style={{ flexDirection: "column", alignItems: "stretch", gap: 12 }}>
                    <div className="tbar" style={{ flexWrap: "wrap" }}>
                        <div className="grow" style={{ position: "relative", minWidth: 280 }}>
                            <Search size={14} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-dim)" }} />
                            <input className="input" style={{ paddingLeft: 34 }} placeholder="Buscar por #ID, asunto, email del cliente o tag…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="tickets-search" />
                        </div>
                        <div style={{ minWidth: 170 }}>
                            <MultiSelect
                                placeholder="Todos los estados"
                                options={TICKET_STATUSES.map(s => ({ value: s.v, label: s.label }))}
                                value={Array.isArray(filters.status) ? filters.status : (filters.status ? filters.status.split(",") : [])}
                                onChange={(arr) => setFilters({ ...filters, status: arr })}
                                testid="tickets-filter-status"
                            />
                        </div>
                        <div style={{ minWidth: 170 }}>
                            <MultiSelect
                                placeholder="Todas las prioridades"
                                options={TICKET_PRIORITIES.map(p => ({ value: p.v, label: p.label }))}
                                value={Array.isArray(filters.priority) ? filters.priority : (filters.priority ? filters.priority.split(",") : [])}
                                onChange={(arr) => setFilters({ ...filters, priority: arr })}
                                testid="tickets-filter-priority"
                            />
                        </div>
                        {isStaff && (
                            <SearchSelect
                                testId="tickets-filter-assignee"
                                placeholder="Cualquier agente"
                                value={filters.assignee}
                                onChange={(v) => setFilters({ ...filters, assignee: v })}
                                specialOptions={[
                                    { value: "", label: "Cualquier agente" },
                                    { value: "me", label: "Asignados a mí" },
                                    { value: "unassigned", label: "Sin asignar" },
                                ]}
                                items={agentList}
                                getKey={(a) => a.user_id}
                                getLabel={(a) => `${a.name} (${a.role})`}
                                getSecondary={(a) => a.email}
                                matchesQuery={(a, q) => a.name.toLowerCase().includes(q) || a.email.toLowerCase().includes(q)}
                                groupLabel="Agentes"
                                width={220}
                            />
                        )}
                        {isStaff && (
                            <SearchSelect
                                testId="tickets-filter-requester"
                                placeholder="Cualquier solicitante"
                                value={filters.requester}
                                onChange={(v) => setFilters({ ...filters, requester: v })}
                                specialOptions={[{ value: "", label: "Cualquier solicitante" }]}
                                items={customerList}
                                getKey={(c) => c.user_id}
                                getLabel={(c) => c.name}
                                getSecondary={(c) => c.email}
                                matchesQuery={(c, q) => c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q)}
                                groupLabel="Solicitantes"
                                width={220}
                            />
                        )}
                        {isStaff && (
                            <SearchSelect
                                testId="tickets-filter-company"
                                placeholder="Cualquier empresa"
                                value={filters.company_id}
                                onChange={(v) => setFilters({ ...filters, company_id: v })}
                                specialOptions={[{ value: "", label: "Cualquier empresa" }]}
                                items={companyList}
                                getKey={(c) => c.company_id}
                                getLabel={(c) => `${c.name}${c.is_placeholder ? " (placeholder)" : ""}${c.is_confidential ? " · Confidencial" : ""}`}
                                getSecondary={(c) => c.domain || ""}
                                matchesQuery={(c, q) => (c.name || "").toLowerCase().includes(q) || (c.domain || "").toLowerCase().includes(q)}
                                groupLabel="Empresas"
                                width={220}
                            />
                        )}
                        {hasAnyFilter && (
                            <button className="btn btn-ghost btn-sm" onClick={() => setFilters({ status: [], priority: [], assignee: "", requester: "", company_id: "", q: "", waiting_state: "", internal: "" })} data-testid="tickets-filter-clear"><Filter size={13} /> Limpiar</button>
                        )}
                        {isStaff && hasAnyFilter && (
                            <button className="btn btn-ghost btn-sm" onClick={saveCurrentView} disabled={savingView} data-testid="tickets-save-view" title="Guarda esta combinación de filtros como vista">
                                {savingView ? <span className="loader" /> : <><Bookmark size={13} /> Guardar vista</>}
                            </button>
                        )}
                    </div>

                    {/* Segmented filters: Semáforo (waiting_state) + Uso Interno */}
                    {isStaff && (
                        <div className="tbar" style={{ flexWrap: "wrap", gap: 12, marginTop: 4 }}>
                            {/* Semáforo: TODOS / Cliente (verde) / Agente (amarillo) / Tercero (rojo) */}
                            <div className="segmented" role="tablist" aria-label="Filtrar por semáforo" data-testid="tickets-segmented-waiting">
                                {[
                                    { v: "", label: "Todos", color: "var(--text-muted)", testid: "todos" },
                                    { v: "customer", label: "Esperando cliente", color: "#10B981", testid: "customer" },
                                    { v: "agent", label: "Esperando agente", color: "#F59E0B", testid: "agent" },
                                    { v: "third_party", label: "Esperando 3ros", color: "#EF4444", testid: "third_party" },
                                ].map(seg => (
                                    <button
                                        key={seg.v || "all"}
                                        role="tab"
                                        aria-selected={filters.waiting_state === seg.v}
                                        className={`segmented-btn ${filters.waiting_state === seg.v ? "is-active" : ""}`}
                                        onClick={() => setFilters({ ...filters, waiting_state: seg.v })}
                                        data-testid={`waiting-seg-${seg.testid}`}
                                        style={{
                                            "--seg-color": seg.color,
                                            color: filters.waiting_state === seg.v ? "#fff" : "var(--text)",
                                            background: filters.waiting_state === seg.v ? seg.color : "transparent",
                                            borderColor: filters.waiting_state === seg.v ? seg.color : "var(--border)",
                                        }}
                                    >
                                        <span style={{
                                            display: "inline-block", width: 8, height: 8, borderRadius: 999,
                                            background: seg.color, marginRight: 6,
                                            boxShadow: filters.waiting_state === seg.v ? "0 0 0 2px rgba(255,255,255,0.35)" : "none",
                                        }} />
                                        {seg.label}
                                    </button>
                                ))}
                            </div>

                            {/* Uso Interno tri-state */}
                            <div className="segmented" role="tablist" aria-label="Filtrar por uso interno" data-testid="tickets-segmented-internal">
                                {[
                                    { v: "", label: "Todos" },
                                    { v: "internal", label: "Internos" },
                                    { v: "external", label: "No internos" },
                                ].map(seg => (
                                    <button
                                        key={seg.v || "all"}
                                        role="tab"
                                        aria-selected={filters.internal === seg.v}
                                        className={`segmented-btn ${filters.internal === seg.v ? "is-active" : ""}`}
                                        onClick={() => setFilters({ ...filters, internal: seg.v })}
                                        data-testid={`internal-seg-${seg.v || "all"}`}
                                        style={{
                                            color: filters.internal === seg.v ? "#fff" : "var(--text)",
                                            background: filters.internal === seg.v ? "var(--accent)" : "transparent",
                                            borderColor: filters.internal === seg.v ? "var(--accent)" : "var(--border)",
                                        }}
                                    >
                                        {seg.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {isStaff && (
                        <div className="tbar" style={{ flexWrap: "wrap", gap: 6 }}>
                            {quickChips.map((c) => (
                                <button key={c.k} className="btn btn-ghost btn-sm" onClick={() => setFilters({ ...filters, ...c.f })} data-testid={`tickets-chip-${c.k}`}>
                                    {c.k}
                                </button>
                            ))}
                            {savedViews.length > 0 && (
                                <span className="text-muted text-xs" style={{ marginLeft: 8, marginRight: 4, alignSelf: "center", textTransform: "uppercase", letterSpacing: ".06em" }}>Mis vistas:</span>
                            )}
                            {savedViews.map((v) => {
                                const cnt = viewCounts[v.view_id];
                                const cntColor = cnt == null ? "var(--text-muted)" : (cnt > 0 ? "var(--accent)" : "var(--text-muted)");
                                return (
                                    <span key={v.view_id} style={{ display: "inline-flex", alignItems: "center", gap: 0 }} data-testid={`tickets-view-${v.view_id}`}>
                                        <button className="btn btn-ghost btn-sm" onClick={() => applyView(v)} style={{ borderRight: "1px solid var(--border)", borderTopRightRadius: 0, borderBottomRightRadius: 0 }} data-testid={`tickets-view-apply-${v.view_id}`}>
                                            <Bookmark size={12} /> {v.name}
                                            <span data-testid={`tickets-view-count-${v.view_id}`} style={{ marginLeft: 6, padding: "1px 7px", borderRadius: 999, fontSize: 11, fontWeight: 700, background: cnt > 0 ? "rgba(220,38,38,.18)" : "var(--bg-2)", color: cntColor, border: `1px solid ${cnt > 0 ? "rgba(220,38,38,.4)" : "var(--border-strong)"}` }}>
                                                {cnt == null ? "…" : cnt}
                                            </span>
                                        </button>
                                        <button
                                            className="btn btn-ghost btn-sm"
                                            onClick={() => toggleViewEmailAlert(v)}
                                            title={v.email_alert_enabled ? "Alertas por email activas · click para desactivar" : "Activar alertas por email cuando la vista crezca"}
                                            style={{ borderRadius: 0, borderRight: "1px solid var(--border)", padding: "0 6px", color: v.email_alert_enabled ? "var(--accent)" : "var(--text-muted)" }}
                                            data-testid={`tickets-view-alert-${v.view_id}`}
                                        >
                                            {v.email_alert_enabled ? <Bell size={12} fill="currentColor" /> : <Bell size={12} style={{ opacity: 0.5 }} />}
                                        </button>
                                        <button className="btn btn-ghost btn-sm" onClick={() => removeView(v)} title="Eliminar vista" style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, padding: "0 6px" }} data-testid={`tickets-view-delete-${v.view_id}`}>
                                            <IconX size={12} />
                                        </button>
                                    </span>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="table-wrap">
                    <table className="t">
                        <thead>
                            <tr>
                                <SortableTh field="ticket_id" sort={sort} onChange={setSort}>ID</SortableTh>
                                <SortableTh field="subject" sort={sort} onChange={setSort}>Asunto</SortableTh>
                                <SortableTh field="status" sort={sort} onChange={setSort}>Estado</SortableTh>
                                <SortableTh field="priority" sort={sort} onChange={setSort}>Prioridad</SortableTh>
                                <SortableTh field="priority_rank" sort={sort} onChange={setSort}>Orden</SortableTh>
                                {showRequesterCol && <SortableTh field="requester_name" sort={sort} onChange={setSort}>Solicitante</SortableTh>}
                                <SortableTh field="company" sort={sort} onChange={setSort}>Empresa</SortableTh>
                                {isStaff && <SortableTh field="assignee_name" sort={sort} onChange={setSort}>Asignado</SortableTh>}
                                <SortableTh field="updated_at" sort={sort} onChange={setSort}>Actualizado</SortableTh>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (<tr><td colSpan={isStaff ? 9 : 7} style={{ textAlign: "center", padding: 30 }}><span className="loader" /></td></tr>)}
                            {!loading && rows.length === 0 && (
                                <tr><td colSpan={isStaff ? 9 : 7} className="text-muted" style={{ textAlign: "center", padding: 40 }}>
                                    No hay tickets que coincidan. <Link to="/tickets/new" className="linklike">Crear uno</Link>.
                                </td></tr>
                            )}
                            {!loading && sortedRows.map((t) => {
                                const s = statusMeta(t.status), p = priorityMeta(t.priority);
                                const comp = t.company_id ? companyById[t.company_id] : null;
                                const wsMap = {
                                    third_party: { color: "#DC2626", label: "🔴 Esperando a un tercero" },
                                    agent: { color: "#F59E0B", label: "🟡 Esperando al agente" },
                                    customer: { color: "#16A34A", label: "🟢 Esperando al cliente" },
                                    closed: { color: "#737373", label: "⚫ Cerrado/resuelto" },
                                };
                                const ws = wsMap[t.responsibility] || wsMap[t.waiting_state] || wsMap.agent;
                                return (
                                    <tr key={t.ticket_id} style={{ cursor: "pointer" }} onClick={() => navigate(`/tickets/${t.ticket_id}`)} data-testid={`ticket-row-${t.ticket_id}`}>
                                        <td className="id-mono">
                                            <span title={ws.label} data-testid={`ticket-waiting-dot-${t.ticket_id}`} style={{ display: "inline-block", width: 8, height: 8, borderRadius: 999, background: ws.color, boxShadow: `0 0 6px ${ws.color}99`, marginRight: 6, verticalAlign: "middle" }} />
                                            <b>{t.ticket_id}</b>
                                        </td>
                                        <td style={{ maxWidth: 420 }}>
                                            <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.subject}</div>
                                            {t.tags?.length > 0 && (
                                                <div style={{ marginTop: 4, display: "flex", gap: 4, flexWrap: "wrap" }}>
                                                    {t.tags.slice(0, 3).map((tag) => <span key={tag} className="badge" style={{ fontSize: 10 }}>{tag}</span>)}
                                                </div>
                                            )}
                                        </td>
                                        <td><span className="badge" style={{ color: s.color, borderColor: s.color + "66", background: s.color + "18" }}>{s.label}</span></td>
                                        <td><span className="badge" style={{ color: p.color, borderColor: p.color + "66", background: p.color + "18" }}>{p.label}</span></td>
                                        <td className="text-muted text-sm mono" data-testid={`ticket-priority-rank-${t.ticket_id}`} style={{ textAlign: "center" }}>
                                            {t.priority_rank != null ? <b style={{ color: "var(--text)" }}>{String(t.priority_rank).padStart(3, "0")}</b> : <span className="text-dim">—</span>}
                                        </td>
                                        {showRequesterCol && <td className="text-muted text-sm"><div>{t.requester_name}</div><div className="mono text-xs">{t.requester_email}</div></td>}
                                        <td className="text-muted text-sm">
                                            {comp ? (
                                                <span>
                                                    {comp.name}
                                                    {comp.is_placeholder && <span className="badge" style={{ marginLeft: 6, fontSize: 10 }}>Placeholder</span>}
                                                    {comp.is_confidential && <span className="badge" style={{ marginLeft: 6, fontSize: 10, background: "#991B1B", color: "#fff" }}>Confidencial</span>}
                                                </span>
                                            ) : (
                                                <span className="text-dim">—</span>
                                            )}
                                        </td>
                                        {isStaff && <td className="text-muted text-sm">{t.assignee_name || <span className="text-dim">sin asignar</span>}</td>}
                                        <td className="text-muted text-xs mono">{formatDateShort(t.updated_at)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
